import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/use-subscription";
import { useOnboarding } from "@/hooks/use-onboarding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface AccountStepProps {
  onNext: () => void;
  onBack: () => void;
}

const ACCOUNT_COLORS = [
  "#10b981", "#3b82f6", "#8b5cf6", "#f59e0b",
  "#ef4444", "#ec4899", "#14b8a6", "#f97316",
];

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

interface AccountTypeOption {
  value: string;
  label: string;
}

const ALL_ACCOUNT_TYPES: AccountTypeOption[] = [
  { value: "checking", label: "Conta Corrente" },
  { value: "savings", label: "Poupança" },
  { value: "credit_card", label: "Cartão de Crédito" },
  { value: "cash", label: "Dinheiro" },
  { value: "investment", label: "Investimentos" },
];

export function AccountStep({ onNext, onBack }: AccountStepProps) {
  const { canAccess } = useSubscription();
  const { completeStep, isUpdating } = useOnboarding();

  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState("");
  const [accountInstitution, setAccountInstitution] = useState("");
  const [accountBalance, setAccountBalance] = useState("");
  const [accountColor, setAccountColor] = useState(ACCOUNT_COLORS[0]);
  const [creditLimit, setCreditLimit] = useState("");
  const [closingDay, setClosingDay] = useState("");
  const [dueDay, setDueDay] = useState("");
  const [loading, setLoading] = useState(false);

  const [hasExistingAccounts, setHasExistingAccounts] = useState(false);
  const [checkingAccounts, setCheckingAccounts] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkExistingAccounts() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          if (!cancelled) setCheckingAccounts(false);
          return;
        }

        const { data } = await supabase
          .from("accounts")
          .select("id")
          .eq("user_id", user.id)
          .limit(1);

        if (!cancelled) {
          setHasExistingAccounts(!!data && data.length > 0);
          setCheckingAccounts(false);
        }
      } catch {
        // On error, default to showing the create form
        if (!cancelled) setCheckingAccounts(false);
      }
    }

    checkExistingAccounts();

    return () => { cancelled = true; };
  }, []);

  const isCreditCard = accountType === "credit_card";

  const availableAccountTypes = ALL_ACCOUNT_TYPES.filter((type) => {
    if (type.value === "savings" && !canAccess("max_savings_accounts")) {
      return false;
    }
    if (type.value === "credit_card" && !canAccess("credit_card_access")) {
      return false;
    }
    return true;
  });

  const isFormValid =
    accountName.trim() !== "" &&
    accountType !== "" &&
    (!isCreditCard || (closingDay !== "" && dueDay !== ""));

  const handleCreateAccount = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const insertData: Record<string, unknown> = {
        user_id: user.id,
        name: accountName.trim(),
        type: accountType,
        institution: accountInstitution.trim() || null,
        balance: parseFloat(accountBalance) || 0,
        color: accountColor,
      };

      if (isCreditCard) {
        insertData.credit_limit = parseFloat(creditLimit) || null;
        insertData.closing_day = parseInt(closingDay) || null;
        insertData.due_day = parseInt(dueDay) || null;
      }

      const { error } = await supabase
        .from("accounts")
        .insert(insertData as any);

      if (error) throw error;

      await completeStep("has_created_first_account");

      toast({
        title: "Conta criada!",
        description: "Sua primeira conta foi configurada com sucesso.",
      });

      onNext();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro ao criar conta",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (checkingAccounts) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (hasExistingAccounts && !showCreateForm) {
    return (
      <div className="space-y-6 py-4">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">
            Você já tem contas cadastradas
          </h2>
          <p className="text-muted-foreground">
            Suas contas existentes serão usadas normalmente.
          </p>
        </div>

        <div className="flex flex-col gap-3 max-w-md mx-auto">
          <Button onClick={onNext}>
            Continuar
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            onClick={() => setShowCreateForm(true)}
          >
            Criar nova conta
          </Button>
        </div>

        <div className="flex justify-start">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold tracking-tight">
          Crie sua primeira conta
        </h2>
        <p className="text-muted-foreground">
          Configure a conta que você mais usa no dia a dia
        </p>
      </div>

      <div className="space-y-4 max-w-md mx-auto">
        <div className="space-y-2">
          <Label htmlFor="account-name">Nome da Conta *</Label>
          <Input
            id="account-name"
            placeholder="Ex: Conta Corrente Principal"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="account-type">Tipo de Conta *</Label>
          <Select value={accountType} onValueChange={setAccountType}>
            <SelectTrigger id="account-type">
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent>
              {availableAccountTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="account-institution">Banco/Instituição</Label>
          <Input
            id="account-institution"
            placeholder="Ex: Nubank, Itaú, C6 Bank..."
            value={accountInstitution}
            onChange={(e) => setAccountInstitution(e.target.value)}
          />
        </div>

        {isCreditCard ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="credit-limit">Limite de Crédito</Label>
              <Input
                id="credit-limit"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="closing-day">Dia de Fechamento *</Label>
                <Select value={closingDay} onValueChange={setClosingDay}>
                  <SelectTrigger id="closing-day">
                    <SelectValue placeholder="Dia" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_OPTIONS.map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Dia que a fatura fecha
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="due-day">Dia de Vencimento *</Label>
                <Select value={dueDay} onValueChange={setDueDay}>
                  <SelectTrigger id="due-day">
                    <SelectValue placeholder="Dia" />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_OPTIONS.map((day) => (
                      <SelectItem key={day} value={day.toString()}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Dia que a fatura vence
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="account-balance">Saldo Inicial</Label>
            <Input
              id="account-balance"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={accountBalance}
              onChange={(e) => setAccountBalance(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Digite o saldo atual desta conta
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Cor de Identificação</Label>
          <div className="flex gap-2 flex-wrap">
            {ACCOUNT_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`h-10 w-10 rounded-full border-2 transition-all ${
                  accountColor === color
                    ? "border-foreground scale-110"
                    : "border-transparent"
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setAccountColor(color)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={loading || isUpdating}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <Button
          onClick={handleCreateAccount}
          disabled={!isFormValid || loading || isUpdating}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Criando...
            </>
          ) : (
            <>
              Criar conta
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
